import { PencilSquareIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { DisbursementSchedUnitMap, DisbursementStartPurMap, noteColors, PaidByInitialMap, SettingKey } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { TaskAssigneeOptions } from 'pages/Tasks/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  type FeeSheetAiExtractResult,
  deleteClosingCost,
  downloadFile,
  downloadHUD1PDF,
  downloadHUD1TemplatePDF,
  getAdminConfig,
  gethud1PageData,
  posthud1Notes,
  posthud1Page1Data,
} from 'services'
import Api from 'services/api'
import svgLoading from 'stories/assets/loading.svg'
import { Button, Checkbox, RadioGroup, Tooltip } from 'stories/components'
import { ClosingCostsDetailsModal } from 'stories/components/ClosingCostsDetailsModal/ClosingCostsDetailsModal'
import { CustomContextMenu } from 'stories/components/CustomContextMenu'
import { FieldNote } from 'stories/components/FieldNote'
import { InterimInterestModal } from 'stories/components/InterimInterestModal'
import { MIPFundingFeeModal } from 'stories/components/MIPFundingFeeModal'
import {
  buildMortgageInsuranceModalData,
  MortgageInsuranceModal,
} from 'stories/components/MortgageInsuranceModal'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { PrepaidItemsDetailsModal } from 'stories/components/PrepaidItemDetailsModal/PrepaidItemDetailsModal'
import {
  confirm,
  convertNegative2Parentheses,
  convertParentheses2Negative,
  formatDate,
  formatDateYMD,
  getDaysDiff,
  getFirstDayofNextMonth,
  getPrice1or2decimal,
  getPrice2decimal,
  getPrice3decimal,
  isEmpty,
  Num,
  openAuditLog,
  removeComma,
} from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'

import { applyPaymentFromEscrowOverrides } from '../AggregateEscrow/escrowPayments'
import { type UnmatchedFee, AddUnmatchedFeesModal } from '../FeeWorkSheet/AddUnmatchedFeesModal'
import { FeeSheetAiExtractModal } from '../FeeWorkSheet/FeeSheetAiExtractModal'
import { getClosingCostInputRows } from '../getClosingCostInputRows'
import { HUD1InputType } from '../HUD1Page1/constant'
import { AddNewFeeModal } from './AddNewFeeModal'
import { ErrorClosingCostsModal } from './ErrorClosingCostsModal'
import { HUD1_700 } from './HUD1_700'
import { HUD1_800 } from './HUD1_800'
import { HUD1_900 } from './HUD1_900'
import { HUD1_1000 } from './HUD1_1000'
import { HUD1_1100 } from './HUD1_1100'
import { HUD1_1200 } from './HUD1_1200'
import { HUD1_1300 } from './HUD1_1300'

export default function HUD1Page2({ pageType = 'hud1Page2' }: { pageType?: string }) {
  const [action, setAction] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [inputs, setInputs] = useState<any>({})
  const [data, setData] = useState<any>({})
  const [orgData, setOrgData] = useState<any>({})
  const [notes, setNotes] = useState<any>({})
  const [IDs, setIDs] = useState<Array<string>>([])
  const [ClosingCosts, setClosingCosts] = useState<Array<Record<string, string>>>([])
  const [PrepaidItems, setPrepaidItems] = useState<Array<Record<string, string>>>([])
  const [Parties, setParties] = useState<Array<Record<string, string>>>([])
  const [changed, setChanged] = useState(false)
  const [ovData, setOVData] = useState<any>({})
  const [closingCostsDetailData, setClosingCostsDetailData] = useState<any>({})
  const [prepaidItemsDetailData, setPrepaidItemsDetailData] = useState<any>({})
  const [mortgageInsuranceDetailData, setMortgageInsuranceDetailData] = useState<any>({})
  const [isAddCCModalOpen, setIsAddCCModalOpen] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorClosingCosts, setErrorClosingCosts] = useState<Array<Record<string, any>>>([])
  const [isNewFeeLoading, setIsNewFeeLoading] = useState<boolean>(false)
  const [ignoreEror, setIgnoreError] = useState<boolean>(true)
  const [isUpdateMonthReserve, setIsUpdateMonthReserve] = useState<boolean>(false)
  const [showMIPFundingModal, setShowMIPFundingModal] = useState(false)
  const [showInterimInterestModal, setShowInterimInterestModal] = useState(false)
  const [isAiExtractOpen, setIsAiExtractOpen] = useState(false)
  const [feeSheetAiEnabled, setFeeSheetAiEnabled] = useState(false)
  const [pendingUnmatched, setPendingUnmatched] = useState<UnmatchedFee[]>([])
  const [isAddUnmatchedOpen, setIsAddUnmatchedOpen] = useState(false)
  const [focusedKey, setFocusedKey] = useState('')
  const [editField, setEditField] = useState({ key: '', value: null })
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    key: '',
    value: null,
  })

  const containerRef = useRef<HTMLDivElement | null>(null)

  const loanNumber = Api.getLoanNumber()
  const { hasPermission } = usePermissions()
  const canAiExtract = hasPermission('Loan.ClosingScreen.FeeWorksheet.AIExtract') && feeSheetAiEnabled
  const calculateKeys = [
    'Loan._HUD1OurOriginationCharge',
    'Loan._HUD1CreditOrChargeForIntRate',
    'Loan._HUD1AdjustedOriginationCharge',
    'Loan._DailyInterest',
    'Loan._InterimInterest',
    'Loan.interimInterestBorrower',
    'Loan._GFEBlockTitleServices',
    'POC.B.Total',
    'POC.S.Total',
    'Loan._GFEBlockRecordingCharges',
    'Loan._GFEBlockTransferTaxes',
    'Loan._GFEBlockServicesYouCanShopFor',
    'HUD1.TotalBorrowerCharges',
    'HUD1.TotalSellerCharges',
    'Loan._GFEBlockEscrowDeposit',
    'Recording Fee Deed',
    'Recording Fee Mortgage',
    'Recording Fee Release',
    'City/Country Deed',
    'City/Country Mortgage',
    'State Deed',
    'State Mortgage',
    'HUD1.disbursementTotal',
    'HUD1.disbursementToBorrower',
    'Loan.AggregateAdjustment',
  ]

  const paymentKeys = [
    'PrepaidItems.0.Payment',
    'PrepaidItems.2.Payment',
    'PrepaidItems.3.Payment',
    'PrepaidItems.4.Payment',
    'PrepaidItems.5.Payment',
    'PrepaidItems.6.Payment',
  ]

  const isHud1a = useMemo(() => pageType == 'hud1A', [pageType])
  const existingLineIds = useMemo(
    () => Array.from(new Set(getClosingCostInputRows(inputs).map((row: any) => Number(row.lineId)).filter(Boolean))),
    [inputs],
  )

  let { liabilities, rulesConfig } = useSelector((state: any) => ({
    liabilities: state.borrower.borrower?.liabilities || [],
    rulesConfig: state.globalConfigReducer[GlobalConfigType.RulesConfig],
  }))
  liabilities = liabilities?.filter((item: any) => item.toBePaid) || []

  useEffect(() => {
    if (!hasPermission('Loan.ClosingScreen.FeeWorksheet.AIExtract')) {
      setFeeSheetAiEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setFeeSheetAiEnabled(!!settings?.feeSheetAiAutofillEnabled)
    })
  }, [])

  const initData = async () => {
    setAction('')
    setIsLoading(true)
    const res = await gethud1PageData('page2')

    if (res.success) {
      setInputs(res.inputs)
      setData(res.data)
      setOrgData(res.data)
      setIDs(res.IDs)
      setClosingCosts(res.ClosingCosts)
      setPrepaidItems(res.PrepaidItems)
      setParties(res.Parties)
      setErrorClosingCosts(res.errorClosingCosts)
      setClosingCostsDetailData({})
      setNotes(res.notes)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  useEffect(() => {
    checkMonthReserve()
  }, [data])

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
    if (!changed) setChanged(true)
  }

  const onBlur = (key: string, value: any, type?: string, orgData?: Record<string, any>) => {
    setFocusedKey('')
    const hasOrgData = !!orgData
    if (!orgData) orgData = data
    let temp = cloneDeep(orgData!)
    const keys = key.split('.')

    if (type === 'number') {
      value = convertParentheses2Negative(value)
      if (key.includes('ClosingCosts') && key.includes('Points'))
        temp[key] = getPrice3decimal(value.replaceAll(',', '')).replaceAll(',', '')
      else temp[key] = getPrice1or2decimal(value.replaceAll(',', '')).replaceAll(',', '')
    } else temp[key] = value

    if (key === 'Status.SchedFundingDate') {
      temp['HUD1.InterimInterestDateFrom'] = value
      temp['HUD1.InterimInterestDateTo'] = getFirstDayofNextMonth(value)
    }
    if (keys[0] === 'ClosingCosts') {
      const pointKey = keys[0] + '.' + keys[1] + '.' + 'Points'
      const borrowerKey = keys[0] + '.' + keys[1] + '.' + 'BorrowerAmount'
      const sellerKey = keys[0] + '.' + keys[1] + '.' + 'SellerAmount'
      if (!temp[pointKey]) {
        if (orgData![pointKey]) {
          temp[borrowerKey] = ''
          temp[sellerKey] = ''
        }
      } else if (temp[pointKey] <= 0) {
        temp[borrowerKey] = 0
      } else {
        temp[borrowerKey] =
          Math.round(
            (Num((Num(temp[`${pointKey}OV`] || temp[pointKey]) * Num(temp['HUD1.Line202Calc'])).toFixed(2)) / 100 -
              Num(temp[sellerKey])) *
              100,
          ) / 100
      }
    }
    if (keys[2] === 'PointsOV') {
      const pointKey = keys[0] + '.' + keys[1] + '.' + 'Points'
      const borrowerKey = keys[0] + '.' + keys[1] + '.' + 'BorrowerAmount'
      const sellerKey = keys[0] + '.' + keys[1] + '.' + 'SellerAmount'
      temp[borrowerKey] =
        Math.round(
          (Num((Num(temp[`${pointKey}OV`]) * Num(temp['HUD1.Line202Calc'])).toFixed(2)) / 100 - Num(temp[sellerKey])) *
            100,
        ) / 100
    }
    if (!hasOrgData) setData(temp)
    else return temp
  }

  const onFocus = (key: string) => {
    const temp = cloneDeep(data)
    temp[key] = convertNegative2Parentheses(data[key])
    setData(temp)
    setFocusedKey(key)
  }

  const handleContextMenu = (event: any, key: string, value: any) => {
    event.preventDefault()
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      setContextMenu({
        visible: true,
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
        key: key,
        value: value,
      })
    }
  }

  useEffect(() => {
    if (contextMenu.visible) {
      document.addEventListener('click', closeContextMenu)
    } else {
      document.removeEventListener('click', closeContextMenu)
    }

    // Cleanup when component unmounts or when context menu is closed
    return () => {
      document.removeEventListener('click', closeContextMenu)
    }
  }, [contextMenu.visible])

  const closeContextMenu = () => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      key: '',
      value: null,
    })
  }

  const onFieldNoteModalSubmit = async (valueKey: string, data: Record<string, any>) => {
    setEditField({ key: '', value: null })
    setIsLoading(true)
    const res = await posthud1Notes(valueKey, data)
    if (res.success) {
      const temp = cloneDeep(notes)
      temp[valueKey] = data
      setNotes(temp)
    }
    setIsLoading(false)
  }

  const onCopyClick = (valueKey: string) => {
    let temp = cloneDeep(data)
    if (valueKey == 'HUD1.InterimInterestDateFrom') temp[valueKey] = data['Status.SchedFundingDate'].split('T')[0]
    else if (valueKey == 'HUD1.InterimInterestDateTo') temp[valueKey] = data['Loan.FirstPaymentDate'].split('T')[0]
    else if (valueKey == 'Loan.InterimInterestDays')
      temp[valueKey] = getDaysDiff(data['HUD1.InterimInterestDateTo'], data['HUD1.InterimInterestDateFrom'])
    setData(temp)
    if (!changed) setChanged(true)
  }

  const closeOVModal = (save: false, data: any) => {
    if (save) {
      onBlur(ovData.overrideKey, data.ovValue)
      setChanged(true)
    }
    setOVData({})
  }

  const closeAddNewFeeModal = (save: false) => {
    setIsAddCCModalOpen(false)
    setIsNewFeeLoading(false)
    if (save) {
      if (changed) onSaveChanges()
      initData()
      toast('Successfully added new fee(s)', { type: 'success' })
    }
  }

  const applyAiExtractFees = (result: FeeSheetAiExtractResult) => {
    const temp = cloneDeep(data)
    let applied = 0
    const rows = getClosingCostInputRows(inputs)
    result.fees.forEach((fee) => {
      const input = rows.find((row: any) => Number(row.lineId) === Number(fee.hudLineNo))
      if (!input) return
      if (fee.borrowerAmount != null) temp[input.feeBKey] = fee.borrowerAmount
      if (fee.sellerAmount != null) temp[input.feeSKey] = fee.sellerAmount
      if (fee.paidToName != null && input.PaidToNameKey) temp[input.PaidToNameKey] = fee.paidToName
      if (fee.poc != null && input.pocKey) temp[input.pocKey] = fee.poc
      applied += 1
    })
    if (result.unmatched?.length) setPendingUnmatched(result.unmatched)
    if (!applied) {
      if (result.unmatched?.length) {
        toast('No matched fees to apply. Use “Add unmatched fees” to add the remaining lines.', { type: 'info' })
      } else {
        toast('No matched fees could be applied to the worksheet.', { type: 'warning' })
      }
      return
    }
    setData(temp)
    setChanged(true)
    toast(`Applied ${applied} fee${applied === 1 ? '' : 's'} from the fee sheet. Save Changes to persist.`, {
      type: 'info',
    })
  }

  const openAddUnmatchedFees = (unmatched: UnmatchedFee[]) => {
    setPendingUnmatched(unmatched)
    setIsAddUnmatchedOpen(true)
  }

  const applyUnmatchedFeeAmounts = async (
    mappings: Array<{ lineId: number; amount: number | null; name: string | null }>,
  ) => {
    setIsLoading(true)
    try {
      const res = await gethud1PageData('page2')
      if (!res.success) {
        toast('Fees were added but the page could not be refreshed.', { type: 'warning' })
        return
      }
      const nextData = cloneDeep(res.data)
      let applied = 0
      const rows = getClosingCostInputRows(res.inputs)
      mappings.forEach(({ lineId, amount, name }) => {
        const input = rows.find((row: any) => Number(row.lineId) === Number(lineId))
        if (!input) return
        if (amount != null) nextData[input.feeBKey] = amount
        if (name && input.titleKey) nextData[input.titleKey] = name
        applied += 1
      })
      setInputs(res.inputs)
      setData(nextData)
      setOrgData(res.data)
      setIDs(res.IDs)
      setClosingCosts(res.ClosingCosts)
      setPrepaidItems(res.PrepaidItems)
      setParties(res.Parties)
      setNotes(res.notes)
      if (applied) setChanged(true)
      setPendingUnmatched([])
      toast(`Added ${applied} unmatched fee${applied === 1 ? '' : 's'}. Save Changes to persist amounts.`, {
        type: 'success',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onDeleteFee = () => {
    initData()
    toast('Successfully deleted fee', { type: 'success' })
  }

  const onErrorModalClose = (cc: any) => {
    setClosingCostsDetailData({
      hudNo: { value: data[cc.lineNoKey], key: cc.lineNoKey },
      lineName: { value: cc.title, key: cc.titleKey },
      isPoints: {
        value:
          (data[cc.lineNoKey] == 801 && [32, 34].includes(data[cc.ClosingCostTypeKey])) || data[cc.lineNoKey] == 802,
        key: 'isPoints',
      },
      points: { value: data[cc.feeMultipleKey], key: cc.feeMultipleKey },
      borrowerAmount: { value: data[cc.feeBKey], key: cc.feeBKey },
      sellerAmount: { value: data[cc.feeSKey], key: cc.feeSKey },
      TRIDBlock: { value: data[cc.TRIDBlockKey], key: cc.TRIDBlockKey },
      GFEBlock: { value: data[cc.GFEBlockKey], key: cc.GFEBlockKey },
      ClosingCostType: { value: data[cc.ClosingCostTypeKey], key: cc.ClosingCostTypeKey },
      PaidToType: { value: data[cc.PaidToTypeKey], key: cc.PaidToTypeKey },
      Financed: { value: data[cc.FinancedKey], key: cc.FinancedKey },
      ResponsiblePartyType: { value: data[cc.ResponsiblePartyTypeKey], key: cc.ResponsiblePartyTypeKey },
      NetFromWire: { value: data[cc.NetFromWireKey], key: cc.NetFromWireKey },
      NotCounted: { value: data[cc.NotCountedKey], key: cc.NotCountedKey },
      ProviderChosenByBorrower: {
        value: data[cc.ProviderChosenByBorrowerKey],
        key: cc.ProviderChosenByBorrowerKey,
      },
      PaidByOtherType: { value: data[cc.PaidByOtherTypeKey], key: cc.PaidByOtherTypeKey },
      poc: { value: data[cc.pocKey], key: cc.pocKey },
      PPFC: { value: data[cc.PPFCKey], key: cc.PPFCKey },
      lineId: { value: data[cc.lineIdKey], key: cc.lineIdKey },
      PointsAndFeesAmountOV: { value: data[cc.PointsAndFeesAmountOVKey], key: cc.PointsAndFeesAmountOVKey },
      PaidTo: { value: data[cc.PaidToNameKey], key: cc.PaidToNameKey },
      isPOCDisabled: { value: cc?.isPOCDisabled, key: '' },
      partyType: { value: cc?.partyType, key: '' },
      partyName: { value: data[cc.defaultValueKey], key: '' },
      ccid: { value: cc.ccid, key: '' },
    })
  }

  const closeCostingDetailModal = (save: false, detailData: any) => {
    if (save) {
      let newData = cloneDeep(data)
      Object.keys(detailData).map((v) => {
        const { key, value } = detailData[v]
        if (!key) return
        if (data[key] !== value) {
          newData = onBlur(key, value, '', newData)
        }
      })
      setData(newData)
      setChanged(true)
    }
    setClosingCostsDetailData({})
    setPrepaidItemsDetailData({})
    setMortgageInsuranceDetailData({})
  }

  const closeMortgageInsuranceModal = (save: false, detailData: any) => {
    if (save) {
      let newData = cloneDeep(data)
      Object.keys(detailData).map((v) => {
        const { key, value } = detailData[v]
        if (!key) return
        if (data[key] !== value) {
          newData = onBlur(key, value, '', newData)
        }
      })
      setData(newData)
      setChanged(true)
    }
    setMortgageInsuranceDetailData({})
  }

  const onSaveChanges = async () => {
    const ccTemp = cloneDeep(ClosingCosts)
    const ppTemp = cloneDeep(PrepaidItems)
    let json: any = {}
    Object.keys(data).map((key) => {
      if (JSON.stringify(data[key]) != JSON.stringify(orgData[key])) {
        json[key] = data[key]
        const params = key.split('.')
        if (params[0] === 'ClosingCosts') ccTemp[+params[1]][params[2]] = data[key]
        if (params[0] === 'PrepaidItems') ppTemp[+params[1]][params[2]] = data[key]
      }
    })
    const paymentChanged = !!Object.keys(json).filter((v) => paymentKeys.includes(v)).length
    if (paymentChanged) {
      const result = await confirm(
        `Due to a change, a revalidation is required.  A task will be generated for the ${rulesConfig.revalidateLoan
          .map((v: any) => TaskAssigneeOptions()[v])
          .join(', ')} to clear this item.`,
      )
      if (!result) return
    }
    calculateKeys.map((v) => {
      json[v] = calculateValue(v)
    })

    const initializedKeys = [inputs['802'][2]?.feeMultipleKey, inputs['802'][2]?.feeBKey]

    initializedKeys.map((v) => {
      json[v] = data[v]
    })
    if (Object.keys(json).length > 0) {
      json.IDs = IDs
      json.ClosingCosts = ClosingCosts
      json.PrepaidItems = PrepaidItems
      json.Parties = Parties
      json.OriginData = orgData
      setAction('saveChanges')
      await posthud1Page1Data('page1', json)
      setAction('')
      setOrgData(data)
      setClosingCosts(ccTemp)
      setPrepaidItems(ppTemp)
      setChanged(false)
    }
    initData()
  }

  const calculateValue = (key: string) => {
    let CALCULATED_VALUE: any = undefined
    const keys: Array<string> = []
    switch (key) {
      case 'Loan._HUD1OurOriginationCharge':
        inputs['originationCharge']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (input.feeBKey) keys.push(input.feeBKey)
          if (input.feeSKey) keys.push(input.feeSKey)
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'Loan._HUD1CreditOrChargeForIntRate':
        const input = inputs['802'][2]
        if (input && (!input.error || ignoreEror))
          CALCULATED_VALUE =
            Math.round(
              Num(
                (
                  Num(data[`${input.feeMultipleKey}OV`] || data[input.feeMultipleKey]) * Num(data['HUD1.Line202Calc'])
                ).toFixed(2),
              ),
            ) / 100
        else CALCULATED_VALUE = 0
        break
      case 'Loan._HUD1AdjustedOriginationCharge':
        CALCULATED_VALUE =
          +(data['Loan._HUD1OurOriginationChargeOV'] || calculateValue('Loan._HUD1OurOriginationCharge')) +
          +(data['Loan._HUD1CreditOrChargeForIntRateOV'] || calculateValue('Loan._HUD1CreditOrChargeForIntRate'))
        break
      case 'Loan._DailyInterest':
        const roundTo = !data['Loan.InterimIntDecimalsOV'] ? 2 : Number(data['Loan.InterimIntDecimalsOV']) + 1
        const loanAmount = data['isDutch'] ? Num(data['HUD1.Line202Calc']) : Num(data['Loan.initialLoanAmount'])
        CALCULATED_VALUE =
          Math.round(
            ((Num(loanAmount) *
              (data['Loan.BDInterimIntCalcMethodOV'] == 3 && data['Loan.LoanProductType'] == 1
                ? Num(data['Loan.ARMIndexValue']) + Num(data['Loan.ARMMargin'])
                : Num(data['Loan.interestRate']))) /
              100 /
              (data['Loan.InterimIntDaysPerYearOV'] == 2 ? 365 : 360)) *
              Math.pow(10, roundTo),
          ) / Math.pow(10, roundTo)
        break
      case 'Loan._InterimInterest':
        if (data['Loan.DailyInterestOV'])
          CALCULATED_VALUE = Num(data['Loan.InterimInterestDays']) * Num(data['Loan.DailyInterestOV'])
        else {
          CALCULATED_VALUE = Num(data['Loan.InterimInterestDays']) * +calculateValue('Loan._DailyInterest')
        }
        CALCULATED_VALUE = Math.round(CALCULATED_VALUE * 100) / 100
        break
      case 'Loan.interimInterestBorrower':
        CALCULATED_VALUE =
          +(data['Loan.InterimInterestOV'] || calculateValue('Loan._InterimInterest') || 0) -
          (Num(data['Loan.InterimInterestPBSDesired']) || 0)
        break
      case 'Loan._GFEBlockTitleServices':
        inputs['titleInsuranceChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (!data[input.pocKey]) {
            keys.push(input.feeBKey)
            keys.push(input.feeSKey)
          }
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'POC.B.Total':
        inputs['titleInsuranceChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input.pocKey] && input.key == 1101) {
            keys.push(input.feeBKey)
          }
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'POC.S.Total':
        inputs['titleInsuranceChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input.pocKey] && input.key == 1101 && data[input.PaidByOtherTypeKey] == 0) {
            keys.push(input.feeSKey)
          }
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'Loan._GFEBlockRecordingCharges':
        inputs['govermentChargesChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (input.feeBKey) keys.push(input.feeBKey)
          if (input.feeSKey) keys.push(input.feeSKey)
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'Loan._GFEBlockTransferTaxes':
        inputs['transferTaxChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (input.feeBKey) keys.push(input.feeBKey)
          if (input.feeSKey) keys.push(input.feeSKey)
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'Loan._GFEBlockServicesYouCanShopFor':
        inputs['1302-1400']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if ([3, 5, 99].includes(Number(data[input['GFEBlockKey']]))) return
          if (!data[input.pocKey]) {
            keys.push(input.feeBKey)
            keys.push(input.feeSKey)
          }
        })
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'HUD1.TotalBorrowerCharges':
        inputs['additionalFees']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (!data[input.pocKey]) {
            keys.push(input.feeBKey)
            keys.push(input.feeSKey)
          }
        })
        ;[...inputs['1102-1104'], ...inputs['1109-1200'], ...inputs['1206-1300'], ...inputs['1302-1400']].map(
          (input: any) => {
            if ((input.error && !ignoreEror) || data[input.pocKey]) return
            if (data[input.GFEBlockKey] == 3 || data[input.GFEBlockKey] == 5) {
              keys.push(input.feeBKey)
              keys.push(input.feeSKey)
            }
            if (data[input.GFEBlockKey] == 99) keys.push(input.feeBKey)
          },
        )
        CALCULATED_VALUE =
          dataSum(keys) +
          dataSum(['HUD1.Line703PBA', 'HUD1.Line704PBA', 'Loan.MIPFFPaidInCash']) +
          calculateSum([
            'Loan._GFEBlockTitleServices',
            'Loan._GFEBlockRecordingCharges',
            'Loan._GFEBlockTransferTaxes',
            'Loan._GFEBlockServicesYouCanShopFor',
            'Loan._HUD1AdjustedOriginationCharge',
            'Loan._GFEBlockEscrowDeposit',
          ]) +
          Num(data['Loan.InterimInterestOV'] || calculateValue('Loan._InterimInterest')) +
          Num(
            !data['PrepaidItems.0.PremiumPOC']
              ? data['PrepaidItems.0.PremiumOV'] ||
                  Num(data['PrepaidItems.0.MonthsInAdvance']) * Num(data['PrepaidItems.0.Payment'])
              : 0,
          ) +
          Num(
            !data['PrepaidItems.5.PremiumPOC']
              ? data['PrepaidItems.5.PremiumOV'] ||
                  Num(data['PrepaidItems.5.MonthsInAdvance']) * Num(data['PrepaidItems.5.Payment'])
              : 0,
          )
        break
      case 'HUD1.TotalSellerCharges':
        ;[...inputs['1102-1104'], ...inputs['1109-1200'], ...inputs['1206-1300'], ...inputs['1302-1400']].map(
          (input: any) => {
            if ((input.error && !ignoreEror) || data[input.pocKey]) return
            if (data[input.GFEBlockKey] == 99) keys.push(input.feeSKey)
          },
        )
        CALCULATED_VALUE = dataSum(['HUD1.Line703PBS', 'HUD1.Line704PBS', ...keys]) || 0.0
        break
      case 'Loan._GFEBlockEscrowDeposit':
        CALCULATED_VALUE = 0
        for (let i = 0; i < 7; i++) {
          const prefix = `PrepaidItems.${i}.`
          if (data[`${prefix}ReservesPOC`]) continue
          CALCULATED_VALUE += data[`${prefix}ReservesOV`]
            ? Num(data[`${prefix}ReservesOV`])
            : Num(data[`${prefix}MonthsInReserve`]) * Num(data[`${prefix}Payment`])
        }
        CALCULATED_VALUE += Num(data['Loan.AggregateAdjustmentOV']) || calculateAggregateAdjustment(data)
        break
      case 'Recording Fee Deed':
        if (data['Recording Fee Deed'] !== undefined) return data['Recording Fee Deed']
        CALCULATED_VALUE = 0
        inputs['govermentChargesChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 24)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'Recording Fee Mortgage':
        if (data['Recording Fee Mortgage'] !== undefined) return data['Recording Fee Mortgage']
        CALCULATED_VALUE = 0
        inputs['govermentChargesChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 35)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'Recording Fee Release':
        if (data['Recording Fee Release'] !== undefined) return data['Recording Fee Release']
        CALCULATED_VALUE = 0
        inputs['govermentChargesChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 46)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'City/Country Deed':
        if (data['City/Country Deed'] !== undefined) return data['City/Country Deed']
        CALCULATED_VALUE = 0
        inputs['transferTaxChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 17)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'City/Country Mortgage':
        if (data['City/Country Mortgage'] !== undefined) return data['City/Country Mortgage']
        CALCULATED_VALUE = 0
        inputs['transferTaxChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 18)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'State Deed':
        if (data['State Deed'] !== undefined) return data['State Deed']
        CALCULATED_VALUE = 0
        inputs['transferTaxChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 49)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'State Mortgage':
        if (data['State Mortgage'] !== undefined) return data['State Mortgage']
        CALCULATED_VALUE = 0
        inputs['transferTaxChilds']?.map((input: any) => {
          if (input.error && !ignoreEror) return
          if (data[input['ClosingCostTypeKey']] == 50)
            CALCULATED_VALUE += Num(data[input['feeBKey']]) + Num(data[input['feeSKey']])
        })
        break
      case 'HUD1.disbursementTotal':
        for (let i = 1501; i < 1516; i++) {
          keys.push('HUD1.Line' + i + 'Amount')
        }
        CALCULATED_VALUE = dataSum(keys)
        break
      case 'HUD1.disbursementToBorrower':
        CALCULATED_VALUE =
          dataSum(['HUD1.Line202Calc', 'HUD1.CashFromBorrower']) -
          Num(calculateValue('HUD1.TotalBorrowerCharges')) -
          Num(calculateValue('HUD1.disbursementTotal'))
        break
      case 'Loan.AggregateAdjustment':
        CALCULATED_VALUE = calculateAggregateAdjustment(data)
        break
      default:
    }
    if (key !== 'Loan._DailyInterest' && CALCULATED_VALUE)
      CALCULATED_VALUE = Number(Number(CALCULATED_VALUE).toFixed(2))
    return CALCULATED_VALUE
  }

  const dataSum = (keys: Array<string>) => {
    let sum = 0
    keys.forEach((key) => {
      if (data[key]) sum += Num(data[key])
    })
    return sum
  }

  const onUpdateMonthReserve = async () => {
    const temp = cloneDeep(data)
    let htmlContent =
      'Would you like to update the number of months in reserves?<div style="font-size: 16px; padding: 0 26px; text-align: left; margin-top: 12px;">'
    const titles = [
      inputs['insurance'][0].title,
      inputs['insurance'][1].title,
      data['PrepaidItems.2.NameOV'],
      inputs['insurance'][2].title,
      inputs['insurance'][3].title,
      inputs['insurance'][4].title,
      data['PrepaidItems.6.NameOV'],
    ]
    const calculatedMonths = calculateMonthReserve()
    calculatedMonths.map((v, index: number) => {
      if (temp[`PrepaidItems.${index}.MonthsInReserve`] != v) {
        htmlContent += `<p>The months in reserve for ${titles[index]} should be ${v}</p>`
      }
    })
    htmlContent += '</div>'
    const result = await confirm(htmlContent)
    if (!result) return
    calculatedMonths.map((v, index: number) => {
      temp[`PrepaidItems.${index}.MonthsInReserve`] = v == 0 ? '' : v
    })
    setData(temp)
    setChanged(true)
  }

  const checkMonthReserve = () => {
    let result = false
    const calculatedMonths = calculateMonthReserve()
    for (let i = 0; i < 7; i++) {
      if (data[`PrepaidItems.${i}.MonthsInReserve`] != calculatedMonths[i]) {
        result = true
        break
      }
    }
    setIsUpdateMonthReserve(result)
  }

  const calculateMonthReserve = () => {
    const firstPaymentMonth = Number(formatDate(data['Loan.FirstPaymentDate'], 'M'))
    const firstPaymentYear = Number(formatDate(data['Loan.FirstPaymentDate'], 'yyyy'))

    const calculatedMonths = []
    for (let i = 0; i < 7; i++) {
      const disbursementSched = +data[`PrepaidItems.${i}.DisbursementSched`]
      const disbursementStartPur = +data[`PrepaidItems.${i}.DisbursementStartPur`]
      const disbursementStartYear = +data[`PrepaidItems.${i}.DisbursementStartYear`]
      const monthInReserve = +data[`PrepaidItems.${i}.MonthsInReserve`]
      const cushion = Number(data[`PrepaidItems.${i}.CushionOV`] || data[`PrepaidItems.${i}._Cushion`]) || 0
      let result: number = 0
      if ([0, 1].includes(Number(disbursementSched))) {
        result = 0
      } else if ([6, 7].includes(Number(disbursementSched))) {
        result = cushion
      } else if ([3, 4, 5].includes(Number(disbursementSched))) {
        if (disbursementStartPur == 0) result = monthInReserve
        else if (disbursementStartPur == 13) result = 1
        else if (
          disbursementStartYear > firstPaymentYear + 1 ||
          (disbursementStartYear == firstPaymentYear + 1 && firstPaymentMonth < disbursementStartPur + 1)
        )
          result = 0
        else {
          let monthsInPeriod = 12
          if (disbursementSched == 3) monthsInPeriod = 3
          else if (disbursementSched == 4) monthsInPeriod = 6

          let min = 100
          for (let i = 0; i < 12 / monthsInPeriod; i++) {
            const currentMonth = (disbursementStartPur + monthsInPeriod * (i - 1)) % 12
            const val = ((firstPaymentMonth % 12) - ((currentMonth + 1) % 12) + 12) % 12
            if (min > val) min = val
          }
          result = min
        }
        result += cushion
      }
      calculatedMonths.push(result)
    }
    return calculatedMonths
  }

  const calculateSum = (keys: Array<string>) => {
    let sum = 0
    keys.forEach((key) => {
      sum += Num(calculateValue(key))
    })
    return sum
  }

  const closeMIPFundingModal = (save: boolean, detailData: any) => {
    if (save) {
      let newData = cloneDeep(data)
      Object.keys(detailData).map((key) => {
        if (data[key] !== detailData[key]) {
          newData = onBlur(key, detailData[key], '', newData)
        }
      })
      setData(newData)
      setChanged(true)
    }
    setShowMIPFundingModal(false)
    setShowInterimInterestModal(false)
  }

  const showInputValue = (value: any, key: string) => {
    if (key !== focusedKey) {
      if (key.includes('ClosingCosts') && key.includes('Points'))
        value = getPrice3decimal(Math.round(value * 1000) / 1000, false, true)
      else value = getPrice2decimal(value, false, true)
      return convertNegative2Parentheses(value)
    }
    return value
  }

  const onFeeDelete = async (ccid: number, lineId: number, title: string) => {
    const content = (
      <>
        <div className="mb-2">Are you sure want to remove this Closing Cost?</div>
        <div className="mb-4 italic">{`${title} (${lineId})`}</div>
      </>
    )
    const isDelete = await confirm(content, {}, 2)
    if (!isDelete) return
    setIsLoading(true)
    await deleteClosingCost(ccid || lineId)
    setIsLoading(false)
    onDeleteFee()
  }

  const updateLiabilities = async () => {
    let htmlContent =
      'Would you like to fill from the liability section of the loan application?<div style="font-size: 16px; padding: 0 46px; text-align: left; margin-top: 12px;">'
    liabilities.map((item: any, index: number) => {
      htmlContent += `<p>${index + 1}. ${item.liability}: $${getPrice1or2decimal(item.balance)}</p>`
    })
    htmlContent += '</div>'
    const result = await confirm(htmlContent)
    if (!result) return
    const temp = cloneDeep(data)
    liabilities.map((item: any, index: number) => {
      temp[`HUD1.Line${index + 1501}Name`] = item.liability
      temp[`HUD1.Line${index + 1501}Amount`] = Number(removeComma(item.balance))
    })
    for (let i = 1501 + liabilities.length; i < 1516; i++) {
      temp[`HUD1.Line${i}Name`] = ''
      temp[`HUD1.Line${i}Amount`] = ''
    }
    setData(temp)
    setChanged(true)
  }

  const renderHUD1Input = (input: any, key?: number) => {
    const {
      type,
      title,
      titleBold,
      titleKey,
      fromKey,
      toKey,
      valueKey,
      overrideKey,
      canOverride,
      defaultValueKey,
      partyType,
      feeMultipleKey,
      feeBKey,
      feeSKey,
      pocKey,
      isChild,
      inputWidth,
      monthKey,
      partyKey,
      TRIDBlockKey,
      GFEBlockKey,
      ClosingCostTypeKey,
      PaidToTypeKey,
      FinancedKey,
      ResponsiblePartyTypeKey,
      NetFromWireKey,
      NotCountedKey,
      ProviderChosenByBorrowerKey,
      PaidByOtherTypeKey,
      PPFCKey,
      lineNoKey,
      lineIdKey,
      PointsAndFeesAmountOVKey,
      PaidToNameKey,
      calculatedValue,
      reserveOVKey,
      contentType,
      PeriodicPaymentAmountKey,
      DisbursementSchedKey,
      DisbursementStartPurKey,
      CushionKey,
      CushionOVKey,
      MonthsInAdvanceKey,
      PremiumOVKey,
      PremiumPBSDesired,
      PremiumNetFromWireKey,
      PremiumPOCKey,
      ReservesPOCKey,
      PremiumPaidToTypeKey,
      PremiumPointsAndFeesAmountOV,
      ReservesPointsAndFeesAmountOV,
      QMATRNotesKey,
      PrepaidItemTypeKey,
      ReservesPaidByOtherTypeKey,
      PremiumPaidByOtherTypeKey,
      MISMOPrepaidItemTypeOVKey,
      DisbursementStartYearKey,
      DisbursementPeriodsKey,
      DisbursementListKey,
      isPOCDisabled,
      ccid,
      error,
    } = input
    if (!ignoreEror && error) return
    let VALUE = valueKey ? data[valueKey] : null
    const OVVALUE = overrideKey ? data[overrideKey] : null
    let CALCULATED_VALUE: any = valueKey ? calculateValue(valueKey) : null
    if (valueKey != 'Loan._DailyInterest') CALCULATED_VALUE = getPrice2decimal(CALCULATED_VALUE, false, true)
    VALUE = !isEmpty(CALCULATED_VALUE) ? CALCULATED_VALUE : calculatedValue || VALUE
    if (type === HUD1InputType.onlyTitle) {
      // only title
      return (
        <div className={`flex gap-2 mt-4 ${titleBold ? 'font-semibold' : ''}`}>
          {!isNaN(Number(key)) && <div className="w-[28px]">{key}.</div>} {title}
        </div>
      )
    }
    if (type === HUD1InputType.titleInputValue) {
      // title + input value
      return (
        <div className="flex flex-wrap justify-end items-center gap-x-4 gap-y-1">
          <div className="flex-1">
            <div className="flex gap-2">
              <div className="w-[28px] cursor-pointer hover:underline" onClick={() => showHistory([valueKey])}>
                {key}.
              </div>{' '}
              {title}
            </div>
          </div>
          <div className="flex-end">
            <input
              className={`px-2 text-right border w-[120px] bg-${noteColors[notes[valueKey]?.flag]} `}
              value={showInputValue(VALUE, valueKey)}
              onBlur={(e) => {
                onBlur(valueKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(valueKey)}
              onChange={(e) => onChange(valueKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, valueKey, data[valueKey])}
            />
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.titleCalculatedValue) {
      // title + calculated value
      return (
        <div className={`flex flex-wrap justify-end items-center gap-x-4 gap-y-1 ${isChild ? 'pl-6' : ''}`}>
          <div className="flex-1 flex justify-between items-center">
            <div className={`flex gap-2 ${titleBold ? 'font-semibold' : ''}`}>
              {key && (
                <div
                  className={`w-[28px] cursor-pointer hover:underline`}
                  onClick={() => showHistory([valueKey, overrideKey].filter((v) => !!v))}
                >
                  {key}.
                </div>
              )}{' '}
              {title}
            </div>
            {canOverride && (
              <span
                className="cursor-pointer hover:text-shade-blue"
                onClick={() =>
                  setOVData({
                    title,
                    overrideKey,
                    calcValue: VALUE,
                    ovValue: data[overrideKey] || '',
                  })
                }
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </span>
            )}
          </div>
          <div>
            <input
              disabled={true}
              className={`px-2 text-right border cursor-not-allowed w-[120px] ${OVVALUE ? 'bg-red-100' : ''} bg-${
                noteColors[notes[valueKey]?.flag]
              }`}
              value={convertNegative2Parentheses(getPrice2decimal(OVVALUE || VALUE, false, true))}
              onContextMenu={(e) =>
                handleContextMenu(
                  e,
                  valueKey,
                  convertNegative2Parentheses(getPrice2decimal(OVVALUE || VALUE, false, true)),
                )
              }
            />
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.inputTitleInputValue) {
      // input title + input value
      return (
        <div className="flex flex-wrap justify-end items-center gap-x-4 gap-y-1">
          <div className="flex flex-1 gap-2">
            <div className="w-[28px]  cursor-pointer hover:underline" onClick={() => showHistory([titleKey, valueKey])}>
              {key}.
            </div>
            <input
              className={`px-2 border flex-1 bg-${noteColors[notes[titleKey]?.flag]}`}
              value={data[titleKey]}
              onChange={(e) => onChange(titleKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, titleKey, data[titleKey])}
            />
          </div>
          <div className="">
            <input
              className={`px-2 text-right border w-[120px] bg-${noteColors[notes[valueKey]?.flag]}`}
              value={showInputValue(VALUE, valueKey)}
              onBlur={(e) => {
                onBlur(valueKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(valueKey)}
              onChange={(e) => onChange(valueKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, valueKey, data[valueKey])}
            />
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.inputFromToValue) {
      // input from & to date + input value
      return (
        <div className="flex flex-wrap justify-end gap-2">
          <div
            className="w-[28px]  cursor-pointer hover:underline"
            onClick={() => showHistory([fromKey, toKey, valueKey])}
          >
            {key}.
          </div>
          <div className="flex flex-wrap flex-1 gap-2 items-center">
            <div className="w-[75px]">{title}</div>
            <input
              className="px-0 border border-gray-200 text-[14px] w-[120px] py-0 placeholder-gray-500"
              value={data[fromKey]}
              type="date"
              onChange={(e) => onChange(fromKey, e.target.value)}
            />
            <span>to</span>
            <input
              className="px-0 border border-gray-200 text-[14px] w-[120px] py-0 placeholder-gray-500"
              value={data[toKey]}
              type="date"
              onChange={(e) => onChange(toKey, e.target.value)}
            />
          </div>
          <div className="">
            <input
              className="px-2 text-right border w-[120px]"
              value={showInputValue(VALUE, valueKey)}
              onBlur={(e) => {
                onBlur(valueKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(valueKey)}
              onChange={(e) => onChange(valueKey, e.target.value)}
            />
          </div>
        </div>
      )
    }
    if (type == HUD1InputType.inputParty) {
      return (
        <div className="flex items-center gap-2">
          <div className="w-[28px] cursor-pointer hover:underline" onClick={() => showHistory([valueKey])}>
            {key}.
          </div>
          <div className="flex gap-2 items-center">
            {title}
            <input
              className={`px-2 text-right border w-[120px] bg-${noteColors[notes[valueKey]?.flag]}`}
              value={showInputValue(data[valueKey], valueKey)}
              onBlur={(e) => {
                onBlur(valueKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(valueKey)}
              onChange={(e) => onChange(valueKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, valueKey, data[valueKey])}
            />
          </div>
          <div className="flex gap-2 items-center">
            <span>to</span>
            <input
              className={`px-2 border w-[240px] bg-${noteColors[notes[defaultValueKey]?.flag]}`}
              value={data[overrideKey] || data[defaultValueKey]}
              disabled={true}
              onContextMenu={(e) => handleContextMenu(e, defaultValueKey, data[overrideKey] || data[defaultValueKey])}
            />
            <span
              className="cursor-pointer hover:text-shade-blue"
              onClick={() =>
                setOVData({
                  title: partyType.name,
                  overrideKey: overrideKey,
                  calcValue: data[defaultValueKey],
                  ovValue: data[overrideKey] || '',
                })
              }
            ></span>
            <Link
              className="flex items-center gap-3 hover:underline hover:text-shade-blue cursor-pointer w-fit"
              to={`/parties/${loanNumber}?edit=${partyType}&redirectTo=${encodeURI(
                `/closing_screen/${loanNumber}?menu=${isHud1a ? 'hud1A' : 'hud1Page2'}`,
              )}`}
            >
              <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
            </Link>
          </div>
        </div>
      )
    }
    if (type == HUD1InputType.inputInputParty) {
      return (
        <div className="flex items-center gap-2">
          <div
            className="w-[28px] cursor-pointer hover:underline"
            onClick={() => showHistory([valueKey, titleKey].filter((v) => !!v))}
          >
            {key}.
          </div>
          <div className="flex gap-2 items-center">
            {title}
            <input
              className="px-2 text-right border w-[120px]"
              value={showInputValue(data[valueKey], valueKey)}
              onBlur={(e) => {
                onBlur(valueKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(valueKey)}
              onChange={(e) => onChange(valueKey, e.target.value)}
            />
          </div>
          <div className="flex gap-2 items-center">
            <span>to</span>
            <input
              className={`px-2 border w-[240px]`}
              value={data[titleKey]}
              onChange={(e) => onChange(titleKey, e.target.value)}
            />
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.title3InputsCheckbox) {
      return (
        <div className={`flex items-center justify-between ${isChild ? 'pl-6' : ''}`}>
          <div className="flex">
            <span
              className="cursor-pointer hover:underline"
              onClick={() =>
                showHistory(
                  [
                    (data[lineNoKey] == 801 && [32, 34].includes(data[ClosingCostTypeKey])) || data[lineNoKey] == 802
                      ? feeMultipleKey
                      : '',
                    feeBKey,
                    feeSKey,
                    pocKey,
                  ].filter((v) => !!v),
                )
              }
            >
              {key}
            </span>
            .
            <span
              className="cursor-pointer"
              onClick={() =>
                setClosingCostsDetailData({
                  hudNo: { value: data[lineNoKey], key: lineNoKey },
                  lineName: { value: title, key: titleKey },
                  isPoints: {
                    value:
                      (data[lineNoKey] == 801 && [32, 34].includes(data[ClosingCostTypeKey])) || data[lineNoKey] == 802,
                    key: 'isPoints',
                  },
                  points: { value: data[feeMultipleKey], key: feeMultipleKey },
                  borrowerAmount: { value: data[feeBKey], key: feeBKey },
                  sellerAmount: { value: data[feeSKey], key: feeSKey },
                  TRIDBlock: { value: data[TRIDBlockKey], key: TRIDBlockKey },
                  GFEBlock: { value: data[GFEBlockKey], key: GFEBlockKey },
                  ClosingCostType: { value: data[ClosingCostTypeKey], key: ClosingCostTypeKey },
                  PaidToType: { value: data[PaidToTypeKey], key: PaidToTypeKey },
                  Financed: { value: data[FinancedKey], key: FinancedKey },
                  ResponsiblePartyType: { value: data[ResponsiblePartyTypeKey], key: ResponsiblePartyTypeKey },
                  NetFromWire: { value: data[NetFromWireKey], key: NetFromWireKey },
                  NotCounted: { value: data[NotCountedKey], key: NotCountedKey },
                  ProviderChosenByBorrower: {
                    value: data[ProviderChosenByBorrowerKey],
                    key: ProviderChosenByBorrowerKey,
                  },
                  PaidByOtherType: { value: data[PaidByOtherTypeKey], key: PaidByOtherTypeKey },
                  poc: { value: data[pocKey], key: pocKey },
                  PPFC: { value: data[PPFCKey], key: PPFCKey },
                  lineId: { value: data[lineIdKey], key: lineIdKey },
                  PointsAndFeesAmountOV: { value: data[PointsAndFeesAmountOVKey], key: PointsAndFeesAmountOVKey },
                  PaidTo: { value: data[PaidToNameKey], key: PaidToNameKey },
                  isPOCDisabled: { value: isPOCDisabled, key: '' },
                  partyType: { value: partyType, key: '' },
                  partyName: { value: data[defaultValueKey], key: '' },
                  ccid: { value: ccid, key: '' },
                })
              }
            >
              <PencilSquareIcon className="w-4 h-4" />
            </span>
            {data[titleKey]}
          </div>
          <div className="flex gap-1">
            {data[lineNoKey] == 802 && (
              <span
                className="cursor-pointer hover:text-shade-blue"
                onClick={() =>
                  setOVData({
                    title,
                    overrideKey: `${feeMultipleKey}OV`,
                    calcValue: data[feeMultipleKey],
                    ovValue: data[`${feeMultipleKey}OV`] || '',
                  })
                }
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </span>
            )}
            <div className="w-[80px]">
              {' '}
              {((data[lineNoKey] == 801 && [32, 34].includes(data[ClosingCostTypeKey])) || data[lineNoKey] == 802) && (
                <input
                  className={`w-full px-2 text-right border ${data[`${feeMultipleKey}OV`] ? 'bg-red-100' : ''} bg-${
                    noteColors[notes[feeMultipleKey]?.flag]
                  }`}
                  value={showInputValue(data[`${feeMultipleKey}OV`] || data[feeMultipleKey], feeMultipleKey)}
                  onBlur={(e) => {
                    onBlur(feeMultipleKey, e.target.value, 'number')
                  }}
                  onFocus={() => onFocus(feeMultipleKey)}
                  onChange={(e) => onChange(feeMultipleKey, e.target.value)}
                  onContextMenu={(e) => handleContextMenu(e, feeMultipleKey, data[feeMultipleKey])}
                  disabled={data[lineNoKey] == 802}
                />
              )}
            </div>
            <input
              className={`px-2 text-right border w-[80px] bg-${noteColors[notes[feeBKey]?.flag]}`}
              value={showInputValue(data[feeBKey], feeBKey)}
              onBlur={(e) => {
                onBlur(feeBKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(feeBKey)}
              onChange={(e) => onChange(feeBKey, e.target.value)}
              disabled={(data[feeMultipleKey] ? true : false) || data[lineNoKey] == 802}
              onContextMenu={(e) => handleContextMenu(e, feeBKey, data[feeBKey])}
            />
            <span>B</span>
            <input
              className={`px-2 text-right border w-[80px] bg-${noteColors[notes[feeSKey]?.flag]}`}
              value={showInputValue(data[feeSKey], feeSKey)}
              onBlur={(e) => {
                onBlur(feeSKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(feeSKey)}
              onChange={(e) => onChange(feeSKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, feeSKey, data[feeSKey])}
            />
            <span>{PaidByInitialMap[data[PaidByOtherTypeKey] || 0]}</span>
            {!isPOCDisabled && (
              <>
                <span>POC</span>
                <Checkbox
                  className={`w-fit`}
                  id={'Calc'}
                  key={'Calc'}
                  value={data[pocKey]}
                  onChange={(e) => onChange(pocKey, e)}
                />
              </>
            )}
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.titlePartyInputs) {
      return (
        <div className={`flex flex-1 items-center justify-between ${isChild ? 'pl-6' : ''}`}>
          <div className="flex">
            <span
              className="cursor-pointer hover:underline"
              onClick={() => showHistory([partyKey, feeBKey, feeSKey].filter((v) => !!v))}
            >
              {key}
            </span>
            .
            <span
              className="cursor-pointer"
              onClick={() =>
                setClosingCostsDetailData({
                  hudNo: { value: data[lineNoKey], key: lineNoKey },
                  lineName: { value: data[titleKey], key: titleKey },
                  isPoints: {
                    value:
                      (data[lineNoKey] == 801 && [32, 34].includes(data[ClosingCostTypeKey])) || data[lineNoKey] == 802,
                    key: 'isPoints',
                  },
                  points: { value: data[feeMultipleKey], key: feeMultipleKey },
                  borrowerAmount: { value: data[feeBKey], key: feeBKey },
                  sellerAmount: { value: data[feeSKey], key: feeSKey },
                  TRIDBlock: { value: data[TRIDBlockKey], key: TRIDBlockKey },
                  GFEBlock: { value: data[GFEBlockKey], key: GFEBlockKey },
                  ClosingCostType: { value: data[ClosingCostTypeKey], key: ClosingCostTypeKey },
                  PaidToType: { value: data[PaidToTypeKey], key: PaidToTypeKey },
                  Financed: { value: data[FinancedKey], key: FinancedKey },
                  ResponsiblePartyType: { value: data[ResponsiblePartyTypeKey], key: ResponsiblePartyTypeKey },
                  NetFromWire: { value: data[NetFromWireKey], key: NetFromWireKey },
                  NotCounted: { value: data[NotCountedKey], key: NotCountedKey },
                  ProviderChosenByBorrower: {
                    value: data[ProviderChosenByBorrowerKey],
                    key: ProviderChosenByBorrowerKey,
                  },
                  PaidByOtherType: { value: data[PaidByOtherTypeKey], key: PaidByOtherTypeKey },
                  poc: { value: data[pocKey], key: pocKey },
                  PPFC: { value: data[PPFCKey], key: PPFCKey },
                  lineId: { value: data[lineIdKey], key: lineIdKey },
                  PointsAndFeesAmountOV: { value: data[PointsAndFeesAmountOVKey], key: PointsAndFeesAmountOVKey },
                  PaidTo: { value: data[PaidToNameKey], key: PaidToNameKey },
                  isPOCDisabled: { value: isPOCDisabled, key: '' },
                  partyType: { value: partyType, key: '' },
                  partyName: { value: data[defaultValueKey], key: '' },
                  ccid: { value: ccid, key: '' },
                })
              }
            >
              <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
            </span>
            {data[titleKey]}
          </div>
          <div className="flex gap-1">
            <div className="flex gap-2 items-center">
              <span>to</span>
              {partyType && (
                <div className="w-[140px] flex gap-1">
                  <span
                    className={`cursor-pointer hover:text-shade-blue flex-1 overflow-hidden bg-${
                      noteColors[notes[PaidToNameKey]?.flag]
                    }`}
                    onClick={() =>
                      setOVData({
                        title: partyType.name,
                        overrideKey: PaidToNameKey,
                        calcValue: data[defaultValueKey],
                        ovValue: data[PaidToNameKey] || '',
                      })
                    }
                    onContextMenu={(e) =>
                      handleContextMenu(e, PaidToNameKey, data[PaidToNameKey] || data[defaultValueKey])
                    }
                  >
                    <span
                      className={`px-2 border cursor-not-allowed inline-flex w-full overflow-auto  no-scrollbar whitespace-nowrap text-overflow-ellipsis ${
                        data[PaidToNameKey] ? 'bg-red-100' : ''
                      }`}
                    >
                      {data[PaidToNameKey] || data[defaultValueKey]}
                    </span>
                  </span>
                  <Link
                    className="flex items-center gap-3 hover:underline hover:text-shade-blue cursor-pointer w-fit"
                    to={`/parties/${loanNumber}?edit=${partyType}&redirectTo=${encodeURI(
                      `/closing_screen/${loanNumber}?menu=${isHud1a ? 'hud1A' : 'hud1Page2'}`,
                    )}`}
                  >
                    <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                  </Link>
                </div>
              )}
              {partyKey && (
                <input
                  className={`px-2 border w-[140px] bg-${noteColors[notes[partyKey]?.flag]}`}
                  value={data[partyKey]}
                  onChange={(e) => onChange(partyKey, e.target.value)}
                  onContextMenu={(e) => handleContextMenu(e, partyKey, data[partyKey])}
                />
              )}
            </div>
            <input
              className={`px-2 text-right border w-[80px] bg-${noteColors[notes[feeBKey]?.flag]}`}
              value={showInputValue(data[feeBKey], feeBKey)}
              onBlur={(e) => {
                onBlur(feeBKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(feeBKey)}
              onChange={(e) => onChange(feeBKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, feeBKey, data[feeBKey])}
            />
            <span>B</span>
            <input
              className={`px-2 text-right border w-[80px] bg-${noteColors[notes[feeSKey]?.flag]}`}
              value={showInputValue(data[feeSKey], feeSKey)}
              onBlur={(e) => {
                onBlur(feeSKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(feeSKey)}
              onChange={(e) => onChange(feeSKey, e.target.value)}
              onContextMenu={(e) => handleContextMenu(e, feeSKey, data[feeSKey])}
            />
            <span>{PaidByInitialMap[data[PaidByOtherTypeKey] || 0]}</span>
            <Tooltip
              message={
                data[pocKey]
                  ? `${data[feeBKey] && data[feeBKey] + 'B*'}${
                      data[feeBKey] && data[feeSKey] && data[PaidByOtherTypeKey] == 0 ? ' + ' : ''
                    }${data[feeSKey] && data[PaidByOtherTypeKey] == 0 ? data[feeSKey] + 'S*' : ''}`
                  : `${getPrice1or2decimal(Num(data[feeBKey]) + Num(data[feeSKey]))}`
              }
            >
              <input
                className="px-2 text-right border w-[50px]"
                value={
                  data[pocKey]
                    ? `${data[feeBKey] && data[feeBKey] + 'B*'}${
                        data[feeBKey] && data[feeSKey] && data[PaidByOtherTypeKey] == 0 ? ' + ' : ''
                      }${data[feeSKey] && data[PaidByOtherTypeKey] == 0 ? data[feeSKey] + 'S*' : ''}`
                    : `${getPrice2decimal(Num(data[feeBKey]) + Num(data[feeSKey]), false, true)}`
                }
                disabled={true}
              ></input>
            </Tooltip>
            {!isPOCDisabled && (
              <>
                <span>POC</span>
                <Checkbox
                  className={`w-fit`}
                  id={'Calc'}
                  key={'Calc'}
                  value={data[pocKey]}
                  onChange={(e) => onChange(pocKey, e)}
                />
              </>
            )}
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.normalInput) {
      return (
        <input
          className={`px-2 text-right border w-[${inputWidth || '120px'}] bg-${noteColors[notes[valueKey]?.flag]}`}
          value={contentType == 'number' ? showInputValue(data[valueKey], valueKey) : data[valueKey]}
          onBlur={(e) => {
            onBlur(valueKey, e.target.value, contentType == 'text' ? '' : 'number')
          }}
          onFocus={() => onFocus(valueKey)}
          onChange={(e) => onChange(valueKey, e.target.value)}
          onContextMenu={(e) => handleContextMenu(e, valueKey, data[valueKey])}
        />
      )
    }
    if (type === HUD1InputType.calculatedInput) {
      const currentValue =
        valueKey == 'Loan._DailyInterest'
          ? convertNegative2Parentheses(OVVALUE || VALUE)
          : convertNegative2Parentheses(getPrice2decimal(OVVALUE || VALUE || 0, false, true))
      let feeBValue
      if (overrideKey.includes('ReservesOV') && data[ReservesPOCKey]) {
        feeBValue = data[reserveOVKey]
          ? data[reserveOVKey] - data[feeSKey]
          : Num((Num(data[feeMultipleKey]) * Num(data[monthKey])).toFixed(2)) - Num(data[feeSKey])
        feeBValue = getPrice2decimal(feeBValue, false, true)
      }
      return (
        <>
          {canOverride && (
            <span
              className="cursor-pointer hover:text-shade-blue"
              onClick={() =>
                setOVData({
                  title,
                  overrideKey,
                  calcValue: VALUE,
                  ovValue: data[overrideKey] || '',
                })
              }
              onContextMenu={(e) => handleContextMenu(e, valueKey, currentValue)}
            >
              <span
                className={`px-2 text-right border cursor-not-allowed inline-block w-[80px] overflow-auto  no-scrollbar whitespace-nowrap text-overflow-ellipsis ${
                  OVVALUE ? 'bg-red-100' : ''
                } bg-${noteColors[notes[valueKey]?.flag]}`}
              >
                {overrideKey.includes('ReservesOV') && data[ReservesPOCKey]
                  ? `${feeBValue}${feeBValue ? 'B*' : ''} ${data[feeSKey]}${data[feeSKey] ? 'S*' : ''}`
                  : currentValue}
              </span>
            </span>
          )}
        </>
      )
    }
    if (type === HUD1InputType.titleInputCalculatedValueInput) {
      return (
        <div className="flex justify-between gap-2">
          <div className="flex flex-1 flex-wrap justify-start items-center gap-x-4 gap-y-1">
            {key && (
              <span
                className="cursor-pointer hover:underline"
                onClick={() =>
                  showHistory([
                    partyKey ? MonthsInAdvanceKey : monthKey,
                    partyKey ? partyKey : feeMultipleKey,
                    feeBKey,
                    feeSKey,
                    reserveOVKey,
                  ])
                }
              >
                {key + '.'}
              </span>
            )}{' '}
            <span
              className="cursor-pointer"
              onClick={() =>
                title === 'Mortgage Insurance'
                  ? setMortgageInsuranceDetailData(
                      buildMortgageInsuranceModalData(data, {
                        MonthsInAdvanceKey,
                        feeMultipleKey,
                        PremiumOVKey,
                        PremiumPBSDesired,
                        PremiumPOCKey,
                        PremiumNetFromWireKey,
                        PremiumPaidByOtherTypeKey,
                        PremiumPaidToTypeKey,
                        PremiumPointsAndFeesAmountOV,
                        monthKey,
                        reserveOVKey,
                        feeSKey,
                        ReservesPOCKey,
                        ReservesPaidByOtherTypeKey,
                        ReservesPointsAndFeesAmountOV,
                        QMATRNotesKey,
                        PeriodicPaymentAmountKey,
                        DisbursementSchedKey,
                        DisbursementStartPurKey,
                        CushionKey,
                        CushionOVKey,
                        DisbursementStartYearKey,
                        DisbursementPeriodsKey,
                        DisbursementListKey,
                      }),
                    )
                  : setPrepaidItemsDetailData({
                    title: [3, 7].includes(data[PrepaidItemTypeKey]) ? data[titleKey] : title,
                    payment: { value: data[feeMultipleKey], key: feeMultipleKey },
                    periodicPayment: { value: data[PeriodicPaymentAmountKey], key: PeriodicPaymentAmountKey },
                    disbSched: { value: data[DisbursementSchedKey], key: DisbursementSchedKey },
                    starting: { value: data[DisbursementStartPurKey], key: DisbursementStartPurKey },
                    cushion: { value: data[CushionKey], key: CushionKey },
                    cushionOV: { value: data[CushionOVKey], key: CushionOVKey },
                    monthsInAdvance: { value: data[MonthsInAdvanceKey], key: MonthsInAdvanceKey },
                    premiumOV: { value: data[PremiumOVKey], key: PremiumOVKey },
                    premiumPBS: { value: data[PremiumPBSDesired], key: PremiumPBSDesired },
                    premiumPOC: { value: data[PremiumPOCKey], key: PremiumPOCKey },
                    netFromWire: { value: data[PremiumNetFromWireKey], key: PremiumNetFromWireKey },
                    paidTo: { value: data[PremiumPaidToTypeKey], key: PremiumPaidToTypeKey },
                    premPointsAndFeesOV: {
                      value: data[PremiumPointsAndFeesAmountOV],
                      key: PremiumPointsAndFeesAmountOV,
                    },
                    monthsInReserve: { value: data[monthKey], key: monthKey },
                    reserveOV: { value: data[reserveOVKey], key: reserveOVKey },
                    reservePBS: { value: data[feeSKey], key: feeSKey },
                    reservesPOC: { value: data[ReservesPOCKey], key: ReservesPOCKey },
                    resPointsAndFeesOV: {
                      value: data[ReservesPointsAndFeesAmountOV],
                      key: ReservesPointsAndFeesAmountOV,
                    },
                    atrNotes: { value: data[QMATRNotesKey], key: QMATRNotesKey },
                    premiumPaidByOther: { value: data[PremiumPaidByOtherTypeKey], key: PremiumPaidByOtherTypeKey },
                    reservePaidByOther: { value: data[ReservesPaidByOtherTypeKey], key: ReservesPaidByOtherTypeKey },
                    prepaidItemType: { value: data[MISMOPrepaidItemTypeOVKey], key: MISMOPrepaidItemTypeOVKey },
                    disbStartYear: { value: data[DisbursementStartYearKey], key: DisbursementStartYearKey },
                    disbPeriods: { value: data[DisbursementPeriodsKey], key: DisbursementPeriodsKey },
                    disbList: { value: data[DisbursementListKey], key: DisbursementListKey },
                  })
              }
            >
              <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
            </span>
            {titleKey ? (
              <>
                <input
                  className={`px-2 text-right border w-[120px]`}
                  value={data[titleKey]}
                  onChange={(e) => onChange(titleKey, e.target.value)}
                />
              </>
            ) : !key ? (
              <span
                className="cursor-pointer hover:underline"
                onClick={() =>
                  showHistory([
                    partyKey ? MonthsInAdvanceKey : monthKey,
                    partyKey ? partyKey : feeMultipleKey,
                    feeBKey,
                    feeSKey,
                    reserveOVKey,
                  ])
                }
              >
                {title}
              </span>
            ) : (
              title
            )}
          </div>
          <div className="flex flex-1 gap-1">
            <input
              className={`px-2 text-right border w-12 bg-${
                noteColors[notes[partyKey ? MonthsInAdvanceKey : monthKey]?.flag]
              }`}
              value={partyKey ? data[MonthsInAdvanceKey] : data[monthKey]}
              onChange={(e) => onChange(partyKey ? MonthsInAdvanceKey : monthKey, e.target.value)}
              onContextMenu={(e) =>
                handleContextMenu(
                  e,
                  partyKey ? MonthsInAdvanceKey : monthKey,
                  partyKey ? data[MonthsInAdvanceKey] : data[monthKey],
                )
              }
            />
            <span>mo </span>
            {partyKey ? (
              <>
                <span>. to</span>
                <input
                  className={`px-2 text-right border w-[120px] bg-${noteColors[notes[partyKey]?.flag]}`}
                  value={data[partyKey]}
                  onChange={(e) => onChange(partyKey, e.target.value)}
                  onContextMenu={(e) => handleContextMenu(e, partyKey, data[partyKey])}
                />
              </>
            ) : (
              <>
                <span>×</span>
                <input
                  className={`px-2 text-right border w-[80px] bg-${noteColors[notes[feeMultipleKey]?.flag]}`}
                  value={showInputValue(data[feeMultipleKey], feeMultipleKey)}
                  onBlur={(e) => {
                    onBlur(feeMultipleKey, e.target.value, 'number')
                  }}
                  onFocus={() => onFocus(feeMultipleKey)}
                  onChange={(e) => onChange(feeMultipleKey, e.target.value)}
                  onContextMenu={(e) => handleContextMenu(e, feeMultipleKey, data[feeMultipleKey])}
                />
                <span>
                  {DisbursementSchedUnitMap[data[DisbursementSchedKey]]}{' '}
                  {[3, 4, 5].includes(data[DisbursementSchedKey]) &&
                    DisbursementStartPurMap[data[DisbursementStartPurKey]]?.slice(0, 3)}
                </span>
              </>
            )}
          </div>
          <div className="flex flex-2 flex-wrap justify-end gap-1">
            <input
              className={`px-2 text-right border w-[80px] bg-${
                noteColors[notes[partyKey ? PremiumOVKey : reserveOVKey]?.flag]
              }`}
              value={getPrice2decimal(
                !partyKey
                  ? data[reserveOVKey]
                    ? Num(data[reserveOVKey]) - Num(data[feeSKey])
                    : Num((Num(data[monthKey]) * Num(data[feeMultipleKey])).toFixed(2)) - Num(data[feeSKey])
                  : data[PremiumOVKey]
                  ? Num(data[PremiumOVKey]) - Num(data[PremiumPBSDesired])
                  : Num((Num(data[MonthsInAdvanceKey]) * Num(data[feeMultipleKey])).toFixed(2)) -
                    Num(data[PremiumPBSDesired]),
                false,
                true,
              )}
              disabled={true}
              onContextMenu={(e) =>
                handleContextMenu(
                  e,
                  partyKey ? PremiumOVKey : reserveOVKey,
                  getPrice2decimal(
                    !partyKey
                      ? data[reserveOVKey]
                        ? Num(data[reserveOVKey]) - Num(data[feeSKey])
                        : Num((Num(data[monthKey]) * Num(data[feeMultipleKey])).toFixed(2)) - Num(data[feeSKey])
                      : data[PremiumOVKey]
                      ? Num(data[PremiumOVKey]) - Num(data[PremiumPBSDesired])
                      : Num((Num(data[MonthsInAdvanceKey]) * Num(data[feeMultipleKey])).toFixed(2)) -
                        Num(data[PremiumPBSDesired]),
                    false,
                    true,
                  ),
                )
              }
            />
            B
            <input
              className={`px-2 text-right border w-[80px] bg-${
                noteColors[notes[partyKey ? PremiumPBSDesired : feeSKey]?.flag]
              }`}
              value={showInputValue(
                data[partyKey ? PremiumPBSDesired : feeSKey],
                partyKey ? PremiumPBSDesired : feeSKey,
              )}
              onBlur={(e) => {
                onBlur(partyKey ? PremiumPBSDesired : feeSKey, e.target.value, 'number')
              }}
              onFocus={() => onFocus(partyKey ? PremiumPBSDesired : feeSKey)}
              onChange={(e) => onChange(partyKey ? PremiumPBSDesired : feeSKey, e.target.value)}
              onContextMenu={(e) =>
                handleContextMenu(
                  e,
                  partyKey ? PremiumPBSDesired : feeSKey,
                  data[partyKey ? PremiumPBSDesired : feeSKey],
                )
              }
            />
            {PaidByInitialMap[data[PremiumPaidByOtherTypeKey]]}
            <span>POC</span>
            <Checkbox
              className={`w-fit`}
              id={'Calc'}
              key={'Calc'}
              value={data[partyKey ? PremiumPOCKey : ReservesPOCKey]}
              onChange={(e) => onChange(partyKey ? PremiumPOCKey : ReservesPOCKey, e)}
            />
          </div>
        </div>
      )
    }
    if (type === HUD1InputType.radioGroup) {
      return (
        <RadioGroup
          id={`option-${title}`}
          name={`option-${title}`}
          title={title}
          options={{
            custom: 'Loan With MIP/FF',
            base: 'Base Loan',
          }}
          value={data[valueKey] == 1 ? 'custom' : 'base'}
          onChange={(v) => onChange(valueKey, v == 'base' ? 0 : 1)}
          className="!pt-0"
          isPlain
          noBorder
          noPadding
        />
      )
    }
  }
  const showHistory = async (keys: Array<string>) => {
    const options = {
      table: 'HUD1',
      field: keys.join(', '),
      keys: {
        field: keys,
      },
      renderValue: (data: any) => <span dangerouslySetInnerHTML={{ __html: data }} />,
    }
    keys = keys.filter((key) => key)
    openAuditLog(options)
  }

  const renderSectionHeader = (title: string, type: string = 'table') => {
    if (type == 'table')
      return (
        <tr>
          <td className="bg-blue-100 p-2">{title}</td>
          <td className="bg-blue-100 p-2">Borrower</td>
          {pageType != 'hud1A' && <td className="bg-blue-100 p-2">Seller</td>}
        </tr>
      )
    else return <p className="bg-blue-100 p-2 md:col-span-4 col-span-1">{title}</p>
  }

  const onDownload = async () => {
    let pdfData, res: any
    if (isHud1a) {
      setIsLoading(true)
      pdfData = await downloadHUD1PDF('hud1A')
    } else {
      res = await confirmOptions('Print HUD1', [
        { name: 'Page 2', value: 0 },
        { name: 'All Pages', value: 1 },
      ])
      if (res === false) return
      setIsLoading(true)
      pdfData = await downloadHUD1PDF(res === 0 ? 'hud1-Page2' : 'hud1-all')
    }
    downloadFile(`HUD1${isHud1a ? 'A' : ''}${res === 0 ? '-Page2' : ''} ${loanNumber}(${formatDateYMD()}).pdf`, pdfData)
    setIsLoading(false)
  }

  const onDownloadTemplate = async () => {
    let pdfData, res: any
    if (isHud1a) {
      setIsLoading(true)
      pdfData = await downloadHUD1TemplatePDF('hud1A')
    } else {
      res = await confirmOptions('Download HUD1 Template', [
        { name: 'Page 2', value: 0 },
        { name: 'All Pages', value: 1 },
      ])
      if (res === false) return
      setIsLoading(true)
      pdfData = await downloadHUD1TemplatePDF(res === 0 ? 'hud1-Page2' : 'hud1-all')
    }
    downloadFile(`HUD1${isHud1a ? 'A' : ''} Template${res === 0 ? '-Page2' : ''} (${formatDateYMD()}).pdf`, pdfData)
    setIsLoading(false)
  }

  return (
    <div className={`HUD1${isHud1a ? 'A' : 'Page2'}-container relative`} ref={containerRef}>
      <LayoutLoading show={action !== '' || isLoading} />
      <Prompt
        when={changed}
        message={`You've made some changes on HUD1 Page2!\nAre you sure want to leave without Saving?`}
      />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center mb-3">
          HUD1{isHud1a ? 'A' : ' Page2'}
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h2>
        <div className="flex gap-2 items-center">
          <p className={`hover:underline cursor-pointer flex justify-end`}>
            <span className="p-1 hover-shadow1 cursor-pointer rounded relative" onClick={onDownloadTemplate}>
              <span className="absolute top-0 right-0 text-red-500 text-xs italic">T</span>
              <Tooltip message="Download Template" position="top" placement="right">
                <PrinterIcon className="w-5 h-5 text-shade-blue" />
              </Tooltip>
            </span>
          </p>
          <p className={`hover:underline cursor-pointer flex justify-end`}>
            <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
              <PrinterIcon className="w-5 h-5 text-shade-blue" />
            </span>
          </p>
        </div>
      </div>
      {errorClosingCosts.length > 0 && !ignoreEror && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-[15.5px]"
          role="alert"
        >
          <div className="font-semibold flex items-center gap-1">
            One or more errors were found.{' '}
            <Button link onClick={() => setIsError(true)} color="blue" className="!m-0 !p-0">
              Click here
            </Button>{' '}
            to correct the errors.
          </div>
        </div>
      )}
      {canAiExtract && (
        <div className="flex items-center gap-3 mb-3">
          <Button link color="blue" onClick={() => setIsAiExtractOpen(true)}>
            Extract fees from PDF
          </Button>
          {pendingUnmatched.length > 0 && (
            <Button link color="blue" onClick={() => setIsAddUnmatchedOpen(true)}>
              Add unmatched fees
            </Button>
          )}
        </div>
      )}
      <div className="my-4 overflow-auto">
        <table className="text-[14px] w-full">
          <tbody>
            {pageType == 'hud1Page2' && (
              <HUD1_700
                inputs={inputs}
                data={data}
                renderSectionHeader={renderSectionHeader}
                renderHUD1Input={renderHUD1Input}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                showInputValue={showInputValue}
                showHistory={showHistory}
                handleContextMenu={handleContextMenu}
                notes={notes}
              />
            )}
            <HUD1_800
              inputs={inputs}
              data={data}
              ignoreEror={ignoreEror}
              isNewFeeLoading={isNewFeeLoading}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              calculateValue={calculateValue}
              setIsNewFeeLoading={setIsNewFeeLoading}
              setIsAddCCModalOpen={setIsAddCCModalOpen}
              isHud1a={isHud1a}
              onFeeDelete={onFeeDelete}
            />
            <HUD1_900
              inputs={inputs}
              data={data}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              onChange={onChange}
              onCopyClick={onCopyClick}
              calculateValue={calculateValue}
              showHistory={showHistory}
              setShowMIPFundingModal={setShowMIPFundingModal}
              setShowInterimInterestModal={setShowInterimInterestModal}
              setOVData={setOVData}
              handleContextMenu={handleContextMenu}
              notes={notes}
            />
            <HUD1_1000
              inputs={inputs}
              data={data}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              calculateValue={calculateValue}
              onUpdateMonthReserve={onUpdateMonthReserve}
              isUpdateMonthReserve={isUpdateMonthReserve}
            ></HUD1_1000>
            <HUD1_1100
              inputs={inputs}
              data={data}
              ignoreEror={ignoreEror}
              isNewFeeLoading={isNewFeeLoading}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              calculateValue={calculateValue}
              setIsNewFeeLoading={setIsNewFeeLoading}
              setIsAddCCModalOpen={setIsAddCCModalOpen}
              isHud1a={isHud1a}
              onFeeDelete={onFeeDelete}
            />
            <HUD1_1200
              inputs={inputs}
              data={data}
              ignoreEror={ignoreEror}
              isNewFeeLoading={isNewFeeLoading}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              calculateValue={calculateValue}
              setIsNewFeeLoading={setIsNewFeeLoading}
              setIsAddCCModalOpen={setIsAddCCModalOpen}
              isHud1a={isHud1a}
              onFeeDelete={onFeeDelete}
            />
            <HUD1_1300
              inputs={inputs}
              data={data}
              ignoreEror={ignoreEror}
              isNewFeeLoading={isNewFeeLoading}
              renderSectionHeader={renderSectionHeader}
              renderHUD1Input={renderHUD1Input}
              calculateValue={calculateValue}
              setIsNewFeeLoading={setIsNewFeeLoading}
              setIsAddCCModalOpen={setIsAddCCModalOpen}
              isHud1a={isHud1a}
              onFeeDelete={onFeeDelete}
            />
            {!isHud1a && (
              <tr>
                <td className="bg-blue-100 p-2">1400. TOTAL SETTLEMENT CHARGES</td>
                {inputs['totalSettlementCharges']?.map((item: any) => {
                  return <td className="bg-blue-100 p-2">{renderHUD1Input(item)}</td>
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {isHud1a && (
        <>
          <div className="grid gap-6 md:grid-cols-4 grid-cols-1 text-[14px]">
            {renderSectionHeader('1500. Disbursements To', 'plain')}
            <div className="md:col-span-4 col-span-1 text-right">
              <button
                className="ml-4 bg-gray-100  rounded-md py-1 px-2 shadow-md hover:drop-shadow-md"
                onClick={updateLiabilities}
              >
                Fill From Liability
              </button>
            </div>
            {inputs['disbursementsTo']?.map((input: any) => {
              return <div className="md:col-span-2 col-span-1">{renderHUD1Input(input, input.key)}</div>
            })}
          </div>
          <div className="grid gap-6 md:grid-cols-4 grid-cols-1 text-[14px] border-double border-gray-300 border-t-2 mt-4 py-4 ">
            <div className="md:col-span-2 col-span-1">
              {inputs['disbursementsTotal'] &&
                renderHUD1Input(inputs['disbursementsTotal'][0], inputs['disbursementsTotal'][0].key)}
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-4 grid-cols-1 text-[14px]">
            {renderSectionHeader('1600. Net Settlement:', 'plain')}
            {inputs['netSettlement']?.map((input: any) => {
              return <div className="md:col-span-2 col-span-1">{renderHUD1Input(input, input.key)}</div>
            })}
          </div>
        </>
      )}
      <div className="flex justify-end">
        <p className={`hover:underline cursor-pointer flex justify-end`}>
          <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
            <PrinterIcon className="w-5 h-5 text-shade-blue" />
          </span>
        </p>
      </div>
      <div className="flex justify-center mt-4">
        <Button disabled={!changed} onClick={onSaveChanges} loading={action === 'saveChanges'}>
          Save Changes
        </Button>
      </div>
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          key={ovData.overrideKey}
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          ovOptions={ovData.ovOptions}
          onClose={closeOVModal}
        />
      )}

      {Object.keys(closingCostsDetailData).length > 0 && (
        <ClosingCostsDetailsModal
          closingCosts={closingCostsDetailData}
          loanNumber={loanNumber}
          loanAmount={data['HUD1.Line202Calc']}
          onClose={closeCostingDetailModal}
          onSubmit={closeCostingDetailModal}
          onDelete={onDeleteFee}
        />
      )}

      {Object.keys(prepaidItemsDetailData).length > 0 && (
        <PrepaidItemsDetailsModal
          prepaidItems={prepaidItemsDetailData}
          onClose={closeCostingDetailModal}
          onSubmit={closeCostingDetailModal}
        />
      )}

      {Object.keys(mortgageInsuranceDetailData).length > 0 && (
        <MortgageInsuranceModal
          modalData={mortgageInsuranceDetailData}
          onClose={closeMortgageInsuranceModal}
          onSubmit={closeMortgageInsuranceModal}
        />
      )}

      {isAddCCModalOpen && (
        <AddNewFeeModal onClose={closeAddNewFeeModal} existingLineIds={existingLineIds}></AddNewFeeModal>
      )}
      {isAiExtractOpen && (
        <FeeSheetAiExtractModal
          isOpen={isAiExtractOpen}
          loanNumber={Number(loanNumber)}
          onClose={() => setIsAiExtractOpen(false)}
          onApply={applyAiExtractFees}
          onAddUnmatched={openAddUnmatchedFees}
        />
      )}
      {isAddUnmatchedOpen && pendingUnmatched.length > 0 && (
        <AddUnmatchedFeesModal
          unmatched={pendingUnmatched}
          existingLineIds={existingLineIds}
          onClose={(saved) => {
            setIsAddUnmatchedOpen(false)
            if (saved) setPendingUnmatched([])
          }}
          onAdded={async (mappings) => {
            if (changed) await onSaveChanges()
            await applyUnmatchedFeeAmounts(mappings)
          }}
        />
      )}

      {isError && (
        <ErrorClosingCostsModal
          errorClosingCosts={errorClosingCosts}
          onClose={() => setIsError(false)}
          onErrorSelect={onErrorModalClose}
          onIngoreError={() => setIgnoreError(true)}
        />
      )}
      {showMIPFundingModal && <MIPFundingFeeModal data={data} onClose={closeMIPFundingModal} />}
      {showInterimInterestModal && <InterimInterestModal data={data} onClose={closeMIPFundingModal} />}

      {contextMenu.visible && (
        <CustomContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={setEditField}
          valueKey={contextMenu.key}
          value={contextMenu.value}
          showHistory={showHistory}
        />
      )}

      {editField.key && (
        <FieldNote
          valueKey={editField.key}
          currentValue={editField.value!}
          noteData={notes[editField.key]}
          onClose={() => setEditField({ key: '', value: null })}
          onOk={onFieldNoteModalSubmit}
        />
      )}

      <SaveChanges show={changed} label="Save Changes" onSave={onSaveChanges} />
    </div>
  )
}

export const calculateAggregateAdjustment = (data: Record<string, any>) => {
  const firstPaymentMonth = Number(formatDate(data['Loan.FirstPaymentDate'], 'M'))
  const firstPaymentYear = Number(formatDate(data['Loan.FirstPaymentDate'], 'yyyy'))
  const closingDateMonth = Number(formatDate(data['Status.SchedClosingDate'], 'M'))
  const closingDateYear = Number(formatDate(data['Status.SchedClosingDate'], 'yyyy'))
  const round2 = (n: number) => Num(n.toFixed(2))

  let totalCush = 0
  let paymentToEscrow: number = 0
  let paymentFromEscrows: Array<number> = []
  for (let i = 0; i < 12; i++) {
    paymentFromEscrows.push(0)
  }
  for (let i = 0; i < 7; i++) {
    const disbursementSched = data[`PrepaidItems.${i}.DisbursementSched`]
    const payment = Number(data[`PrepaidItems.${i}.Payment`])
    const disbursementStartPur = data[`PrepaidItems.${i}.DisbursementStartPur`]
    const disbursementStartYear = data[`PrepaidItems.${i}.DisbursementStartYear`]
    const disbursementPeriods = data[`PrepaidItems.${i}.DisbursementPeriods`]
    const disbursementList = data[`PrepaidItems.${i}.DisbursementList`]?.split('|')
    const cushion = data[`PrepaidItems.${i}.CushionOV`] || data[`PrepaidItems.${i}._Cushion`] || 0
    const exactPeriodicAmount = data[`PrepaidItems.${i}.PeriodicPaymentAmount`]
    if (disbursementSched > 1) totalCush = round2(totalCush + round2(Num(cushion) * Num(payment)))

    if (disbursementSched > 1) paymentToEscrow = round2(paymentToEscrow + payment)
    if (disbursementSched == 2) {
      for (let j = 0; j < 12; j++) {
        paymentFromEscrows[j] = round2(paymentFromEscrows[j] + payment)
      }
    } else if ([3, 4, 5].includes(Number(disbursementSched))) {
      if (disbursementStartPur == 0) continue
      let startMonth, startYear
      if (disbursementStartPur > 0 && disbursementStartPur < 13) {
        startMonth = disbursementStartPur
        startYear = disbursementStartYear
      } else {
        startMonth = closingDateMonth
        startYear = closingDateYear
      }
      let monthsInPeriod = 12
      let tolerance = 0.1
      if (disbursementSched == 3) {
        monthsInPeriod = 3
        tolerance = 0.025
      } else if (disbursementSched == 4) {
        monthsInPeriod = 6
        tolerance = 0.05
      }

      for (let j = (startMonth - firstPaymentMonth + 12) % 12; j < 12; j += monthsInPeriod) {
        if (!startYear || firstPaymentYear + (j + firstPaymentMonth > 12 ? 1 : 0) >= startYear) {
          let originPeriodicAmount = round2(Num(payment) * Num(monthsInPeriod))
          const exactPeriodicAmountNum = Num(exactPeriodicAmount)
          if (Math.round(Math.abs(originPeriodicAmount - exactPeriodicAmountNum) * 1000) / 1000 <= tolerance)
            originPeriodicAmount = exactPeriodicAmountNum
          paymentFromEscrows[j] = round2(paymentFromEscrows[j] + originPeriodicAmount)
        }
      }
    } else if (disbursementSched == 6) {
      for (let j = 0; j < 12; j++) {
        if (disbursementPeriods[j] != '.' && !!disbursementPeriods[j]) {
          const index = (j + 1 - firstPaymentMonth + 12) % 12
          paymentFromEscrows[index] = round2(
            paymentFromEscrows[index] +
              round2(Num(payment) * Num(disbursementPeriods[j].charCodeAt(0) - 'A'.charCodeAt(0) + 1)),
          )
        }
      }
    } else if (disbursementSched == 7) {
      disbursementList?.map((v: any) => {
        const [date, amount] = v.split('~')
        const month = new Date(date).getMonth() + 1
        let year = new Date(date).getFullYear()
        if (month < firstPaymentMonth) year--
        if (year == firstPaymentYear) {
          const index = (month - firstPaymentMonth + 12) % 12
          paymentFromEscrows[index] = round2(paymentFromEscrows[index] + Num(amount))
        }
      })
    }
  }
  paymentFromEscrows = applyPaymentFromEscrowOverrides(data, paymentFromEscrows)
  let minBal = 1000000000
  let balance = 0
  for (let i = 0; i < 12; i++) {
    balance = round2(balance + paymentToEscrow - paymentFromEscrows[i])
    if (minBal > balance) minBal = balance
  }

  let sum = round2(totalCush - minBal)
  for (let i = 0; i < 7; i++) {
    const prefix = `PrepaidItems.${i}.`
    if (['0', '1', '', 0, 1].includes(data[`${prefix}DisbursementSched`])) continue
    const monthInReserve = Num(data[`${prefix}MonthsInReserve`])
    const payment = Num(data[`${prefix}Payment`])
    const result = round2(Num(monthInReserve) * Num(payment))
    sum = round2(sum - result)
  }

  return sum
}

/** Display Aggregate Adjustment with HUD1-style red highlight when overridden. */
export const renderAggregateAdjustmentValue = (
  data: Record<string, any>,
  { useParentheses = true }: { useParentheses?: boolean } = {},
) => {
  const calculated = calculateAggregateAdjustment(data)
  const ovRaw = data['Loan.AggregateAdjustmentOV']
  const hasOverride = ovRaw !== undefined && ovRaw !== null && String(ovRaw).trim() !== ''
  const format = (n: number) => {
    const formatted = getPrice2decimal(n, false, true)
    return useParentheses ? convertNegative2Parentheses(formatted) : formatted
  }
  const display = format(hasOverride ? Num(ovRaw) : Num(calculated) || 0)
  if (!hasOverride) return display
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span className="bg-red-100 px-1">{display}</span>
      <span className="text-[10px] text-gray-500 leading-tight">calc: {format(Num(calculated) || 0)}</span>
    </span>
  )
}
